import { Address } from "./User";

export interface Proof {
  proofOfInsuranceProvidedByUserId: number;
  alerts: {
    Level: string;
    Message: string;
    LID: number;
  }[];
  status: "pending";
  id: number;
  companyName: string;
  policyNumber: string;
  fileUri: string;
  startDate: string;
  endDate: string;
  isVerified: boolean;
  strStartDate: string;
  strEndDate: string;
  strIsVerified: string;
}

export interface Plan {
  id: number;
  buyerUserId: number;
  additionalUserIds: number[];
  policyNumber: string;
  buyerName: string;
  startDate: string;
  endDate: string;
  numberOfPayments: number;
  purchaseAmount: number;
  premium: number;
  alerts: {
    Level: string;
    Message: string;
    LID: number;
  }[];
  policyProvider: string;
  provider: {
    name: string;
    logoUrl: string;
    siteUrl: string;
    phoneNumber: string;
    supportUrl: string;
  };
  status: string;
  paymentAccountId: number;
  additionalUsers: {
    leaseLivlyUserId: number;
    firstName: string;
    lastName: string;
    leaseLivlyUserTypeId: number;
    userId: number;
    avatarUri: string;
  }[];
  coverages: {
    label: string;
    value: string;
    decimalValue: number;
  }[];
  nextPaymentAmount: number;
  nextPaymentDate: string;
  deductible: number;
  contactPhone: string;
  contactEmail: string;
  address: {
    id: number;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    latitude: number;
    longitude: number;
    isLeaseAddress: boolean;
  };
  documentUri: string;
}

export interface AssurantQuote {
  configuration: AssurantQuoteConfiguration;
  presentation: AssurantQuotePresentation;
  purchase: AssurantQuotePurchase;
}

export interface AssurantQuoteConfiguration {
  deductibleAllowed: Array<number>;
  personalLiabilityAllowed: Array<number>;
  personalPropertyIncr: string;
  personalPropertyMax: string;
  personalPropertyMin: string;
  policyEffectiveDate: string;
}

export interface AssurantQuotePresentation {
  additionalInsuredUserId?: number | null;
  billingAddress?: Address | null;
  coverageLiability: string;
  coveragePersonalProperty: string;
  deductible: string;
  denialNotice?: null;
  isPaperless?: boolean;
  isPlanAdded?: boolean;
  messageText: string;
  numberOfPayments?: string | null;
  okToText?: boolean | null;
  riskState: string;
  stateDisclosure: string;
  fraudNotice: string;
  totalPremium: string;
  hurricaneDeductible?: string;
  coverageSewerBackup: string;
  fraudExpenseProtection?: string;
  earthquake?: string;
}

export interface AssurantQuotePurchase {
  schedules: Array<AssurantQuotePurchaseSchedule>;
}
export interface AssurantQuotePurchaseSchedule {
  accountPlanCode: string;
  billPlanCode: string;
  description: string;
  downPaymentAmount: string;
  installmentAmount?: number | null;
  numberOfPayments: number;
  serviceFee?: number | null;
}

export interface AssurantQuoteV2 {
  quoteResponse: {
    transactionDetails: AssurantQuoteV2TransactionDetails;
    bundling: AssurantQuoteV2Bundling;
    multiLine: AssurantQuoteV2MultiLine;
    premiumDetails: AssurantQuoteV2PremiumDetails;
    policyHolderDetails: AssurantQuoteV2PolicyHolderDetails;
    coveragesCue: AssurantQuoteV2CoveragesCue;
    coverageDetails: AssurantQuoteV2CoverageDetails;
  };
  quoteRequest: RequoteRequestModelV2;
  selectionMetaData?: SelectionMetaData;
}

export interface AssurantQuoteV2TransactionDetails {
  responseMessage: string;
  denialNotice: string | null;
  workItemId: string;
  clientId: string;
  referralId: string;
  productId: string;
  policyEffectiveDate: string;
  policyEnteredDate: string;
  maxEffectiveDays: string;
  masterCompany: string;
  companyBlock: string;
  quoteNumber: string | null;
  premium: string | number | null;
  payments: {
    schedules: AssurantQuoteV2PaymentSchedule[];
  };
}

export interface AssurantQuoteV2PaymentSchedule {
  numberOfPayments: number;
  description: string;
  downPaymentAmount: number;
  installmentAmount: number | null;
  serviceFee: number | null;
}

export interface AssurantQuoteV2Bundling {
  isBundled: boolean | null;
  isAutoPolicy: boolean | null;
  autoPolicyNumber: string | null;
}

export interface AssurantQuoteV2MultiLine {
  hasCredit: boolean | null;
  creditAmount: number | null;
  creditCarrier: string | null;
  policyNumber: string | null;
}

export interface AssurantQuoteV2PremiumDetails {
  premiumAmount: string;
}

export interface AssurantQuoteV2PolicyHolderDetails {
  insured: {
    insuredName: string | null;
    address: AssurantQuoteV2Address;
  };
  lineOfBusiness: string;
}

export interface AssurantQuoteV2Address {
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

export interface AssurantQuoteV2CoveragesCue {
  businessRules: {
    rule300KLiability: {
      initialUrl: string;
      outcomeUrl: string;
    };
  }[];
  parameters: AssurantQuoteV2CoveragesCueParameter[];
}

export interface AssurantQuoteV2CoverageDetails {
  parameters: AssurantQuoteV2Parameter[];
}

// Known parameter names and their types for AssurantQuoteV2Parameter
export type AssurantQuoteV2ParameterName =
  | "hasIui"
  | "hasIdFraud"
  | "hasPetDamage"
  | "hasWaterBackup"
  | "hasWaterDamageLiability"
  | "hasReplacementCost"
  | "hasEarthquake"
  | "iuiTypeOptions"
  | "iuiNumberOfBenefitsOptions"
  | "iuiBenefitAmountOptions"
  | "hasInternetCredit"
  | "hasLossFreeCredit"
  | "hasConstructionType"
  | "hasHurricaneDeductible"
  | "personalLiabilityOptions"
  | "deductibleOptions"
  | "personalPropertyMin"
  | "personalPropertyMax"
  | "personalPropertyIncrement"
  | "hasFloodIncluded"
  | "lossFreeCredit"
  | "hurricaneDeductible"
  | "liabilityAmount"
  | "isMasonryConstruction"
  | "personalPropertyAmount"
  | "replacementCost"
  | "waterBackup"
  | "earthquake"
  | "dwellingCreditSurcharge"
  | "internetCredit"
  | "identityFraud"
  | "mustHaveInsurance"
  | "petDamage"
  | "waterDamageLiability"
  | "deductible"
  | "iuiTypes"
  | "iuiNumberOfBenefits"
  | "iuiBenefitAmount"
  | "floodIncluded"
  | (string & {}); // fallback for unknown names

// Mapper for parameter value types
export type AssurantQuoteV2ParameterValueMap = {
  hasIui: string;
  hasIdFraud: string;
  hasPetDamage: string;
  hasWaterBackup: string;
  hasWaterDamageLiability: string;
  hasReplacementCost: string;
  hasEarthquake: string;
  iuiTypeOptions: string;
  iuiNumberOfBenefitsOptions: string;
  iuiBenefitAmountOptions: string | null;
  hasInternetCredit: string;
  hasLossFreeCredit: string;
  hasConstructionType: string;
  hasHurricaneDeductible: string;
  personalLiabilityOptions: number[];
  deductibleOptions: number[];
  personalPropertyMin: string;
  personalPropertyMax: string;
  personalPropertyIncrement: string;
  hasFloodIncluded: string;
  lossFreeCredit: string;
  hurricaneDeductible: string | null;
  liabilityAmount: string;
  isMasonryConstruction: string;
  personalPropertyAmount: string;
  replacementCost: string;
  waterBackup: string;
  earthquake: string;
  dwellingCreditSurcharge: string;
  internetCredit: string;
  identityFraud: string;
  mustHaveInsurance: string;
  petDamage: string;
  waterDamageLiability: string;
  deductible: string;
  iuiTypes: string;
  iuiNumberOfBenefits: string;
  iuiBenefitAmount: string;
  floodIncluded: string;
  // fallback for unknown names
  [key: string]: string | number | number[] | null;
};

export interface AssurantQuoteV2Parameter {
  name: AssurantQuoteV2ParameterName;
  value: AssurantQuoteV2ParameterValueMap[AssurantQuoteV2Parameter["name"]];
}

// Known coveragesCue parameter names and their types
export type AssurantQuoteV2CoveragesCueParameterName =
  | "hasIui"
  | "hasIdFraud"
  | "hasPetDamage"
  | "hasWaterBackup"
  | "hasWaterDamageLiability"
  | "hasReplacementCost"
  | "hasEarthquake"
  | "iuiTypeOptions"
  | "iuiNumberOfBenefitsOptions"
  | "iuiBenefitAmountOptions"
  | "hasInternetCredit"
  | "hasLossFreeCredit"
  | "hasConstructionType"
  | "hasHurricaneDeductible"
  | "personalLiabilityOptions"
  | "deductibleOptions"
  | "personalPropertyMin"
  | "personalPropertyMax"
  | "personalPropertyIncrement"
  | "hasFloodIncluded"
  | (string & {}); // fallback for unknown names

export type AssurantQuoteV2CoveragesCueParameterValueMap = {
  hasIui: string;
  hasIdFraud: string;
  hasPetDamage: string;
  hasWaterBackup: string;
  hasWaterDamageLiability: string;
  hasReplacementCost: string;
  hasEarthquake: string;
  iuiTypeOptions: string;
  iuiNumberOfBenefitsOptions: string;
  iuiBenefitAmountOptions: string | null;
  hasInternetCredit: string;
  hasLossFreeCredit: string;
  hasConstructionType: string;
  hasHurricaneDeductible: string;
  personalLiabilityOptions: number[];
  deductibleOptions: number[];
  personalPropertyMin: string;
  personalPropertyMax: string;
  personalPropertyIncrement: string;
  hasFloodIncluded: string;
  [key: string]: string | number | number[] | null;
};

export interface AssurantQuoteV2CoveragesCueParameter {
  name: AssurantQuoteV2CoveragesCueParameterName;
  value: AssurantQuoteV2CoveragesCueParameterValueMap[AssurantQuoteV2CoveragesCueParameter["name"]];
}

export type RequoteRequestModelV2 = {
  transaction: {
    clientId: string;
    productType: string;
    workItemId: string;
    jobNumber: string;
    policyEffectiveDate: string;
    referralId: string;
  };
  policyHolder: {
    insured: {
      address: AssurantQuoteV2Address;
    };
  };
  underWriting: {
    questions: Array<{
      questionName: string;
      answer: string;
    }>;
  };
  coverage: {
    parameters: Array<{
      name: string;
      value: string;
    }>;
  };
};

export interface Disclosure {
  DocumentName: string;
  Content: string;
}

export enum DisclosureType {
  Fraud = 0,
  ElectronicBusinessConsent,
  Legal,
  Underwriters,
  privacyPolicy,
  Regulatory,
  Accessibility,
  CoverageDescription,
  ConsumerRights,
  PayAuthorization,
  CancellationNotice,
  Sms,
  InterestedParty,
  WaterSewerBackup
}

export interface SelectionMetaData {
  additionalInsuredUserId: number;
  numberOfPayments: string;
  billingAddress: Address;
  isPaperless: boolean;
  okToText: boolean;
  isPlanAdded: boolean;
  uwAnswers: null | string;
}
